import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

// Images - user uploaded photos for gallery
const IMAGES = {
  about: "https://customer-assets.emergentagent.com/job_duet-dining/artifacts/y4kk1rj8_image00001.jpeg",
  whoWeAre: "https://customer-assets.emergentagent.com/job_duet-dining/artifacts/e4cw8f0l_c55feeb0b87f681428efc6e9676c920f.jpg",
  quotes: "https://customer-assets.emergentagent.com/job_duet-dining/artifacts/d653poek_7dcd8ec50c9b2dfc833ecd871eba4587.jpg",
  gallery: [
    "https://customer-assets.emergentagent.com/job_duet-dining/artifacts/2gf5dqua_d143b78410ae8c00d02a10a7f16da6da.jpg",
    "https://customer-assets.emergentagent.com/job_duet-dining/artifacts/swmympjm_8a4d328815a6ce9d3bd64f60d6d2a911.jpg",
    "https://customer-assets.emergentagent.com/job_duet-dining/artifacts/g8zuvwf7_ae4465542c17d4d9f4880980aff9d244.jpg",
    "https://customer-assets.emergentagent.com/job_duet-dining/artifacts/s8ch4bfy_164ec8194299267e476a8c40785d1631.jpg",
    "https://customer-assets.emergentagent.com/job_duet-dining/artifacts/hxa20nar_c55feeb0b87f681428efc6e9676c920f.jpg",
    "https://customer-assets.emergentagent.com/job_duet-dining/artifacts/0qyd1732_337370d70fdeca7a36cdb3da56197dce.jpg",
    "https://customer-assets.emergentagent.com/job_duet-dining/artifacts/04b3eov4_image.png",
    "https://customer-assets.emergentagent.com/job_duet-dining/artifacts/g7u7n55z_image.png",
    "https://customer-assets.emergentagent.com/job_duet-dining/artifacts/krn047ob_image.png",
    "https://customer-assets.emergentagent.com/job_duet-dining/artifacts/lw6a7uwy_image.png",
  ]
};

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: "easeOut" }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.15
    }
  }
};

export default function HomePage() {
  const [lightboxImage, setLightboxImage] = useState(null);

  // Close lightbox on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") setLightboxImage(null);
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, []);

  return (
    <div className="min-h-screen bg-cream">
      <Header />

      {/* Two Clickable Blocks Section - Direct Impact Start */}
      <section data-testid="blocks-section" className="pt-24 md:pt-28 pb-0 px-4 md:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
          {/* Who We Are Block */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Link
              to="#about"
              data-testid="who-we-are-block"
              className="group block aspect-[3/2] md:aspect-[3/4] relative overflow-hidden"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              <img
                src={IMAGES.whoWeAre}
                alt="Wie wij zijn"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              {/* Subtle dark overlay for text readability on bright image */}
              <div className="absolute inset-0 bg-black/45 group-hover:bg-black/55 transition-all duration-500" />
              {/* Text */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center px-8 py-6">
                  <h2 className="text-white text-2xl md:text-3xl lg:text-4xl tracking-[0.2em] uppercase font-light drop-shadow-lg">
                    Wie wij zijn
                  </h2>
                </div>
              </div>
            </Link>
          </motion.div>

          {/* Quotes Block */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <Link
              to="/offerte"
              data-testid="quotes-block"
              className="group block aspect-[3/2] md:aspect-[3/4] relative overflow-hidden"
            >
              <img
                src={IMAGES.quotes}
                alt="Offerte"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              {/* Subtle dark overlay for text readability */}
              <div className="absolute inset-0 bg-black/45 group-hover:bg-black/55 transition-all duration-500" />
              {/* Text */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center px-8 py-6">
                  <h2 className="text-white text-2xl md:text-3xl lg:text-4xl tracking-[0.2em] uppercase font-light drop-shadow-lg">
                    Offerte
                  </h2>
                </div>
              </div>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" data-testid="about-section" className="py-20 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-6xl mx-auto">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center"
          >
            <motion.div variants={fadeInUp} className="order-2 lg:order-1">
              <h2 className="font-heading text-3xl md:text-4xl text-burgundy mb-8 tracking-wide">
                Over Duet
              </h2>
              <div className="space-y-6 text-burgundy/80 leading-relaxed">
                <p>
                  Duet Dining begint met een tafel.
                </p>
                <p>
                  Een lange tafel het liefst. Kaarsen aan. Glazen die al klaarstaan. De eerste mensen die binnenlopen met dat kleine beetje nieuwsgierigheid dat altijd bij een avond als deze hoort.
                </p>
                <p>
                  Wij zijn Demi en Pam. Twee vrouwen die geloven dat een goed diner verder gaat dan wat er op het bord ligt.
                </p>
                <p>
                  Demi kookt. Vanuit seizoenen, herinneringen en alles wat haar onderweg inspireert. In haar keuken ontstaat een menu dat meebeweegt met de avond.
                </p>
                <p>
                  Pam zorgt voor de muziek. Ze bouwt een soundtrack die de ruimte vult en de tafel langzaam in beweging brengt.
                </p>
                <p>
                  Samen maken we avonden waarop mensen dichter bij elkaar komen te zitten, gesprekken vanzelf langer worden en niemand haast heeft om op te staan.
                </p>
                <p>
                  Dat is Duet Dining.
                </p>
              </div>
              <motion.div variants={fadeInUp} className="mt-10">
                <Link
                  to="/offerte"
                  data-testid="about-cta-button"
                  className="btn-primary inline-block"
                >
                  Vraag offerte aan
                </Link>
              </motion.div>
            </motion.div>
            
            <motion.div 
              variants={fadeInUp} 
              className="order-1 lg:order-2"
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src={IMAGES.about}
                  alt="Duet Dining tafel setting"
                  className="w-full h-full object-cover image-hover"
                />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Memories Section */}
      <section data-testid="memories-section" className="py-20 md:py-32 px-6 md:px-12 lg:px-24 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-sm tracking-[0.3em] uppercase text-burgundy/60 mb-4">
              Onze momenten
            </h2>
            <h3 className="font-heading text-3xl md:text-5xl text-burgundy tracking-wide font-light">
              Memories
            </h3>
          </motion.div>

          <div className="marquee-container -mx-4 md:-mx-8 lg:-mx-16">
            <div className="marquee-track">
              {[...IMAGES.gallery, ...IMAGES.gallery].map((img, index) => (
                <div
                  key={index}
                  className="marquee-item group"
                  onClick={() => setLightboxImage(img)}
                  data-testid={index < IMAGES.gallery.length ? `gallery-image-${index}` : undefined}
                >
                  <img
                    src={img}
                    alt={`Memory ${(index % IMAGES.gallery.length) + 1}`}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How to Book Section */}
      <section data-testid="how-to-book-section" className="py-20 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
          >
            <h3 className="font-heading text-3xl md:text-5xl text-burgundy mb-16 tracking-wide">
              Hoe verloopt jouw aanvraag
            </h3>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: "-50px" }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12 mb-16"
          >
            {[
              { number: "01", text: "Vraag een offerte aan" },
              { number: "02", text: "We nemen persoonlijk contact op" },
              { number: "03", text: "We creëren samen jullie avond" },
              { number: "04", text: "We verzorgen food, muziek & styling" },
            ].map((step, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="text-center"
              >
                <span className="font-light text-4xl md:text-5xl text-burgundy block mb-4">
                  {step.number}
                </span>
                <p className="text-burgundy/80 text-sm md:text-base">
                  {step.text}
                </p>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <Link 
              to="/offerte"
              data-testid="how-to-book-cta-button"
              className="btn-primary inline-block"
            >
              Start jouw aanvraag
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxImage && (
          <motion.div
            data-testid="lightbox"
            className="lightbox-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightboxImage(null)}
          >
            <button 
              data-testid="lightbox-close"
              className="absolute top-6 right-6 text-white hover:text-blue transition-colors"
              onClick={() => setLightboxImage(null)}
            >
              <X size={32} />
            </button>
            <motion.img
              src={lightboxImage}
              alt="Gallery preview"
              className="max-w-[90vw] max-h-[90vh] object-contain"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
